/**
 * Domain enumerations, verbatim from BACKEND-SPEC §1.
 *
 * Every one of these is mirrored by a Postgres enum in `@seo/db`; the drizzle schema
 * imports these arrays so the two can never drift.
 */

export const PAYMENT_METHOD_TYPES = ['card', 'local', 'ewallet', 'voucher', 'crypto'] as const;
export type PaymentMethodType = (typeof PAYMENT_METHOD_TYPES)[number];

export const CATALOG_SOURCES = ['api', 'parser', 'csv', 'manual'] as const;
export type CatalogSource = (typeof CATALOG_SOURCES)[number];

export const PARSER_STATUSES = ['active', 'paused', 'error'] as const;
export type ParserStatus = (typeof PARSER_STATUSES)[number];

export const LICENSE_TYPES = ['local', 'offshore'] as const;
export type LicenseType = (typeof LICENSE_TYPES)[number];

export const EEAT_RISKS = ['low', 'medium', 'high'] as const;
export type EeatRisk = (typeof EEAT_RISKS)[number];

export const RG_ORG_TYPES = ['support', 'self_exclusion', 'education', 'regulatory'] as const;
export type RgOrgType = (typeof RG_ORG_TYPES)[number];

export const TEXT_DIRECTIONS = ['ltr', 'rtl'] as const;
export type TextDirection = (typeof TEXT_DIRECTIONS)[number];

export const DICTIONARY_CATEGORIES = [
  'bonuses',
  'payments',
  'gameplay',
  'legal',
  'seo',
  'rg',
] as const;
export type DictionaryCategory = (typeof DICTIONARY_CATEGORIES)[number];

export const CONTENT_SOURCES = ['ai', 'manual'] as const;
export type ContentSource = (typeof CONTENT_SOURCES)[number];

export const BONUS_TYPES = [
  'welcome',
  'deposit_n',
  'reload',
  'cashback',
  'free_spins',
  'no_deposit',
  'vip',
  'promocode',
  'seasonal',
  'birthday',
  'referral',
  'custom',
] as const;
export type BonusType = (typeof BONUS_TYPES)[number];

export const BONUS_SOURCES = ['manual', 'ai_parsed'] as const;
export type BonusSource = (typeof BONUS_SOURCES)[number];

export const BRAND_COLOR_ROLES = ['primary', 'secondary', 'accent', 'bg', 'custom'] as const;
export type BrandColorRole = (typeof BRAND_COLOR_ROLES)[number];

export const BRAND_ASSET_SLOTS = [
  'logo',
  'favicon',
  'main_banner',
  'secondary_banner_1',
  'secondary_banner_2',
  'secondary_banner_3',
  'mobile_screenshot',
  'signup_form',
] as const;
export type BrandAssetSlot = (typeof BRAND_ASSET_SLOTS)[number];

/**
 * Вимога до кожного слота — текстом, який бачить оператор.
 *
 * Слоти ассетів у мокапі (рядки 4520–4525) — повноцінний тип поля, а не масив файлів:
 * фіксований іменований перелік, текст вимог під кожним і видимий стан «✗ немає». Порожній
 * слот — робочий стан, а не помилка, і саме тому список фіксований: «медіа заповнено на 3 з
 * 8» — відповідь, а «завантажено 3 файли» — ні.
 *
 * Живе в контрактах, бо це те саме, що пише `brand_assets.spec` при створенні бренду і що
 * показує картка. Дві копії розійшлися б на першій зміні розміру банера.
 */
export const BRAND_ASSET_SPECS: Record<BrandAssetSlot, string> = {
  logo: 'SVG або PNG 240×80, прозорий фон',
  favicon: 'PNG 512×512 або SVG, квадрат',
  main_banner: 'JPG/PNG 1280×420',
  secondary_banner_1: 'JPG/PNG 640×360',
  secondary_banner_2: 'JPG/PNG 640×360',
  secondary_banner_3: 'JPG/PNG 640×360',
  mobile_screenshot: 'PNG 750×1334, вертикальний',
  signup_form: 'PNG 800×600, знімок форми реєстрації',
};

export const ASSET_STATES = ['present', 'missing'] as const;
export type AssetState = (typeof ASSET_STATES)[number];

export const GEO_SCOPE_KINDS = ['all', 'geo'] as const;
export type GeoScopeKind = (typeof GEO_SCOPE_KINDS)[number];

export const PAGE_TYPES = [
  'home',
  'bonus',
  'registration',
  'app',
  'games',
  'game',
  'provider',
  'payments',
  'review',
  'about',
  'faq',
  'blog',
  'landing',
  'contacts',
  'responsible_gaming',
  'terms',
  'privacy',
] as const;
export type PageType = (typeof PAGE_TYPES)[number];

export const BRAND_PAGE_TYPE_STATUSES = ['live', 'queued', 'off', 'fixed'] as const;
export type BrandPageTypeStatus = (typeof BRAND_PAGE_TYPE_STATUSES)[number];

export const GAME_TYPES = ['slot', 'crash', 'live', 'arcade', 'table'] as const;
export type GameType = (typeof GAME_TYPES)[number];

export const VOLATILITIES = ['low', 'medium', 'high', 'very_high'] as const;
export type Volatility = (typeof VOLATILITIES)[number];

export const SITE_TYPES = ['monobrand', 'monoslot', 'review_cluster', 'drop'] as const;
export type SiteType = (typeof SITE_TYPES)[number];

export const DOMAIN_TYPES = ['emd', 'drop', 'generic'] as const;
export type DomainType = (typeof DOMAIN_TYPES)[number];

export const ENTITY_TYPES = ['brand', 'game', 'cluster'] as const;
export type SiteEntityType = (typeof ENTITY_TYPES)[number];

/**
 * Тип сайту йде за сутністю, на якій сайт стоїть (§1.5) — і це та сама відповідність, яку
 * має знати клієнт, коли створює шаблон під сайт прямо у візарді.
 *
 * У контрактах, а не в сервісі, бо інакше правд стає дві: сервер підставляє `monobrand`
 * бренду, а майстер створює шаблон під `monoslot`, і оператор дізнається про розходження
 * аж тоді, коли шаблон не підходить сайту.
 */
export const SITE_TYPE_BY_ENTITY: Record<SiteEntityType, SiteType> = {
  brand: 'monobrand',
  game: 'monoslot',
  cluster: 'review_cluster',
};

export const SITE_STATUSES = ['build', 'live', 'frozen', 'error'] as const;
export type SiteStatus = (typeof SITE_STATUSES)[number];

export const CANONICAL_TARGETS = ['self', 'drop', 'site'] as const;
export type CanonicalTarget = (typeof CANONICAL_TARGETS)[number];

export const HREFLANG_ROLES = ['lang', 'x-default', 'none'] as const;
export type HreflangRole = (typeof HREFLANG_ROLES)[number];

export const SITE_PAGE_STATUSES = [
  'draft',
  'live',
  'generating',
  'waiting_media',
  'off',
] as const;
export type SitePageStatus = (typeof SITE_PAGE_STATUSES)[number];

export const REDIRECT_CODES = [301, 302] as const;
export type RedirectCode = (typeof REDIRECT_CODES)[number];

export const REDIRECT_SOURCES = ['drop_history', 'dedup', 'offer'] as const;
export type RedirectSource = (typeof REDIRECT_SOURCES)[number];

export const CLUSTER_TYPES = ['mini_review', 'top10', 'editorial'] as const;
export type ClusterType = (typeof CLUSTER_TYPES)[number];

export const CLUSTER_OFFER_STATUSES = ['active', 'cap', 'paused', 'geo_blocked'] as const;
export type ClusterOfferStatus = (typeof CLUSTER_OFFER_STATUSES)[number];

export const KEYWORD_OWNER_TYPES = ['brand', 'game', 'cluster', 'site'] as const;
export type KeywordOwnerType = (typeof KEYWORD_OWNER_TYPES)[number];

export const KEYWORD_SOURCES = ['gsc', 'csv', 'manual', 'competitor'] as const;
export type KeywordSource = (typeof KEYWORD_SOURCES)[number];

export const KEYWORD_AI_STATUSES = [
  'ok',
  'new_page',
  'duplicate',
  'cannibalization',
  'no_geo',
] as const;
export type KeywordAiStatus = (typeof KEYWORD_AI_STATUSES)[number];

export const DATA_SOURCES = [
  'brand.bonuses',
  'brand.assets',
  'brand.socials',
  'geo.payments',
  'geo.legal',
  'games.top',
  'games.selected',
  'reference.providers',
  'reference.licenses',
  'reference.rg',
  'cluster.offers',
  'site.internal_links',
  'ai',
] as const;
export type DataSource = (typeof DATA_SOURCES)[number];

export const BLOCK_MODES = ['data', 'ai', 'manual'] as const;
export type BlockMode = (typeof BLOCK_MODES)[number];

export const PROMPT_PROVIDERS = ['openrouter', 'openai', 'anthropic'] as const;
export type PromptProvider = (typeof PROMPT_PROVIDERS)[number];

export const PROMPT_STATES = ['live', 'draft', 'fixed'] as const;
export type PromptState = (typeof PROMPT_STATES)[number];

export const ANCHOR_TYPES = ['brand', 'exact', 'generic', 'naked', 'url', 'commercial'] as const;
export type AnchorType = (typeof ANCHOR_TYPES)[number];

export const DONOR_STATUSES = ['new', 'approved', 'rejected', 'blacklist'] as const;
export type DonorStatus = (typeof DONOR_STATUSES)[number];

export const GUEST_POST_AUDIT_STATUSES = [
  'FOUND_OK',
  'FOUND_TOO_SHORT',
  'FOUND_NO_ANCHOR',
  'NOT_FOUND',
] as const;
export type GuestPostAuditStatus = (typeof GUEST_POST_AUDIT_STATUSES)[number];

export const GUEST_POST_STATUSES = [
  'topic_ai',
  'draft',
  'generating',
  'ready',
  'rewrite',
  'exported',
] as const;
export type GuestPostStatus = (typeof GUEST_POST_STATUSES)[number];

export const GUEST_POST_ANCHOR_KINDS = ['anchor', 'anchorless'] as const;
export type GuestPostAnchorKind = (typeof GUEST_POST_ANCHOR_KINDS)[number];

export const INTEGRATION_KINDS = [
  'gsc',
  'scraper',
  'provider_parser',
  'ai_key',
  'tracker',
  'dns',
  'notifications',
  'registrar',
  'hosting',
] as const;
export type IntegrationKind = (typeof INTEGRATION_KINDS)[number];

export const INTEGRATION_STATUSES = ['active', 'paused', 'error'] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];

export const JOB_STATUSES = [
  'queued',
  'active',
  'completed',
  'failed',
  'delayed',
  'dead',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const BUILD_STEPS = ['domain', 'entity', 'geo', 'pages', 'template', 'assemble'] as const;
export type BuildStep = (typeof BUILD_STEPS)[number];

export const BUILD_STATUSES = ['draft', 'ready', 'building', 'built', 'deployed', 'error'] as const;
export type BuildStatus = (typeof BUILD_STATUSES)[number];

export const BULK_OPERATIONS = [
  'canonical',
  'hreflang',
  'relink',
  'redirects',
  'add_folder',
  'regenerate',
  'translate',
  'replace_offer',
  'redeploy',
] as const;
export type BulkOperation = (typeof BULK_OPERATIONS)[number];

export const BULK_STATUSES = [
  'draft',
  'dry_run',
  'awaiting_confirmation',
  'running',
  'done',
  'rolled_back',
  'failed',
] as const;
export type BulkStatus = (typeof BULK_STATUSES)[number];

export const RISK_LEVELS = ['low', 'medium', 'high'] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

export const ABUSE_SOURCES = ['dmca', 'cloudflare', 'hosting', 'registrar', 'google'] as const;
export type AbuseSource = (typeof ABUSE_SOURCES)[number];

/**
 * Джерела, які приходять ззовні листом і яких система спостерігати не може.
 *
 * Решта трьох — хостинг, реєстратор і Google — виводяться з інцидентів, знімків індексації
 * та дат реєстрації, тож їх заводить `detectAbuse`. Розділення тут, а не в роутері, бо його
 * читають обидві сторони: форма реєстрації в UI і валідація входу.
 */
export const EXTERNAL_ABUSE_SOURCES = ['dmca', 'cloudflare'] as const;
export type ExternalAbuseSource = (typeof EXTERNAL_ABUSE_SOURCES)[number];

/** Людські підписи джерел — мокап показує «ХОСТ» і «РЕЄСТР», а не `hosting` / `registrar`. */
export const ABUSE_SOURCE_LABELS: Record<AbuseSource, string> = {
  dmca: 'DMCA',
  cloudflare: 'CF',
  hosting: 'ХОСТ',
  registrar: 'РЕЄСТР',
  google: 'GOOGLE',
};

export const ABUSE_STATUSES = [
  'new',
  'response',
  'migration',
  'rewrite',
  'closed',
  'frozen',
] as const;
export type AbuseStatus = (typeof ABUSE_STATUSES)[number];

export const INCIDENT_KINDS = ['down', 'index_drop', 'deploy_anomaly'] as const;
export type IncidentKind = (typeof INCIDENT_KINDS)[number];

export const DOMAIN_STATUSES = ['free', 'assigned', 'expired'] as const;
export type DomainStatus = (typeof DOMAIN_STATUSES)[number];

export const DOMAIN_SOURCES = ['pool', 'emd'] as const;
export type DomainPoolSource = (typeof DOMAIN_SOURCES)[number];

export const HOSTING_POOL_TYPES = ['sftp', 's3', 'api', 'cloudflare'] as const;
export type HostingPoolType = (typeof HOSTING_POOL_TYPES)[number];

export const REGISTRAR_PROVIDERS = ['namecheap', 'porkbun', 'dynadot', 'godaddy'] as const;
export type RegistrarProvider = (typeof REGISTRAR_PROVIDERS)[number];

export const EMD_AVAILABILITY = ['free', 'for_sale', 'taken'] as const;
export type EmdAvailability = (typeof EMD_AVAILABILITY)[number];

export const EMD_RISK_FLAGS = ['brand_conflict', 'typosquat', 'bad_zone'] as const;
export type EmdRiskFlag = (typeof EMD_RISK_FLAGS)[number];

/**
 * Operator-facing names for the risk flags.
 *
 * Here rather than in the screen, because the purchase refusal quotes them too: a UI-only
 * copy would drift from the reason the API actually gives.
 */
export const EMD_RISK_FLAG_LABELS: Record<EmdRiskFlag, string> = {
  brand_conflict: 'чужий бренд',
  typosquat: 'тайпсквот',
  bad_zone: 'погана зона',
};

export const PARSE_SOURCE_KINDS = ['url', 'text', 'file', 'competitors', 'api'] as const;
export type ParseSourceKind = (typeof PARSE_SOURCE_KINDS)[number];

export const PARSE_ACTIONS = ['write', 'review', 'conflict'] as const;
export type ParseAction = (typeof PARSE_ACTIONS)[number];

export const PARSE_ENTITY_TYPES = [
  'brand',
  'game',
  'geo',
  /**
   *  — провайдери ігор, і довго це був єдиний довідник, який парсер умів читати.
   * Платіжки, ліцензії та RG мають власні типи, а не спільний: у них різні колонки, і
   * спільний тип означав би алоулист із обʼєднання трьох таблиць — дозвіл писати в поле,
   * якого в цієї сутності немає.
   */
  'reference',
  'payment_method',
  'license',
  'rg_organization',
  'cluster',
  'site',
  'donor',
] as const;
export type ParseEntityType = (typeof PARSE_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'publish',
  'revert',
  'deploy',
  'bulk',
  'login',
  'role_change',
  'import',
  'unlock',
  'parse_apply',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const EXCHANGE_RATE_SOURCES = ['manual', 'ecb'] as const;
export type ExchangeRateSource = (typeof EXCHANGE_RATE_SOURCES)[number];

export const MEDIA_OWNER_SLOTS = ['brand', 'game', 'payment_method', 'provider', 'license', 'rg', 'template', 'page_block'] as const;
export type MediaOwnerType = (typeof MEDIA_OWNER_SLOTS)[number];

export const QUEUE_NAMES = [
  'generation',
  'translation',
  'parsing',
  'deploy',
  'gsc',
  'monitoring',
  'links-audit',
  'emd',
  'notifications',
  'system',
  'dead',
] as const;
export type QueueName = (typeof QUEUE_NAMES)[number];

/** Додаток C — job kind per queue. */
export const JOB_KINDS = [
  'generate_text',
  'translate',
  'rewrite',
  'generate_topics',
  'generate_guest_post',
  'parse_entity',
  'brand_scrape',
  'catalog_sync',
  'assemble_site',
  'render_site',
  'deploy_site',
  'gsc_sync_property',
  'gsc_sync_all',
  'ping_batch',
  'indexation_scan',
  'seo_score_batch',
  'audit_links_run',
  'emd_check_candidates',
  'emd_purchase',
  'notify_send',
  'db_backup',
  'retention_cleanup',
  'dashboard_refresh',
  'exchange_rates_sync',
  'brand_autoparse',
  'dlq_digest',
] as const;
export type JobKind = (typeof JOB_KINDS)[number];

export const JOB_KIND_QUEUE: Record<JobKind, QueueName> = {
  generate_text: 'generation',
  rewrite: 'generation',
  generate_topics: 'generation',
  generate_guest_post: 'generation',
  translate: 'translation',
  parse_entity: 'parsing',
  brand_scrape: 'parsing',
  catalog_sync: 'parsing',
  assemble_site: 'deploy',
  render_site: 'deploy',
  deploy_site: 'deploy',
  gsc_sync_property: 'gsc',
  gsc_sync_all: 'gsc',
  ping_batch: 'monitoring',
  indexation_scan: 'monitoring',
  seo_score_batch: 'monitoring',
  audit_links_run: 'links-audit',
  emd_check_candidates: 'emd',
  emd_purchase: 'emd',
  notify_send: 'notifications',
  db_backup: 'system',
  retention_cleanup: 'system',
  dashboard_refresh: 'system',
  exchange_rates_sync: 'system',
  brand_autoparse: 'parsing',
  dlq_digest: 'system',
};
